import React, { useState, useEffect, useRef, useContext, useCallback, lazy, Suspense } from 'react';
import { collection, addDoc, query, where, orderBy, onSnapshot, doc, setDoc, getDocs, getDoc, limit, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { UserContext } from '../context/UserContext';
import Layout from '../components/Layout';
import '../css/MessagesPage.css';
import { useSearchParams, useNavigate } from 'react-router-dom';
import SharedPostLink from '../UI-Components/SharedPostLink/SharedPostLink';
import { Avatar } from '@mui/material';
import { ArrowBack, MoreVert, EmojiEmotions, Close } from '@mui/icons-material';
import SendIcon from '../assets/icons/CTA.svg';

// Lazy load emoji picker to reduce initial bundle size (~200KB savings)
const EmojiPicker = lazy(() => import('@emoji-mart/react'));
const getEmojiData = () => import('@emoji-mart/data').then(mod => mod.default);

const MessagesPage = () => {
    const navigate = useNavigate();
    const { user } = useContext(UserContext);
    const [emojiData, setEmojiData] = useState(null); // Lazy loaded emoji data
    const [searchParams] = useSearchParams();
    const groupId = searchParams.get('group');
    const startChatWith = searchParams.get('startChat');
    const [isMobile, setIsMobile] = useState(true);
    const [mobileView, setMobileView] = useState('list'); // 'list' or 'chat'
    const [friendSearchQuery, setFriendSearchQuery] = useState('');
    
    //Open group chat if groupId is provided
    useEffect(() => {
        if (groupId) {
            setSelectedChat(groupId); // Open the group chat
            if (isMobile) setMobileView('chat');
        }
    }, [groupId, isMobile]);

    // Handle startChat from Friends page
    useEffect(() => {
        if (startChatWith) {
            startConversation(startChatWith);
            if (isMobile) setMobileView('chat');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startChatWith, isMobile]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 1200);
        };

        // Set initially
        handleResize();

        // Add resize listener
        window.addEventListener("resize", handleResize);

        // Cleanup on unmount
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const [conversations, setConversations] = useState([]);
    const [selectedChat, setSelectedChat] = useState(null);
    const [messages, setMessages] = useState([]);
    const [allFriends, setAllFriends] = useState([]);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [senderNames, setSenderNames] = useState({});
    const [senderProfilePics, setSenderProfilePics] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const userCache = useRef({});

    const dropdownRef = useRef(null); // Ref to track clicks outside dropdown

    const toggleDropdown = () => {
        setIsDropdownOpen(!isDropdownOpen);
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };

        if (isDropdownOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isDropdownOpen]);

    useEffect(() => {
        const fetchFriends = async () => {
            if (!user) return;
            try{
                console.log("Fetching users >>>");
                const userRef = doc(db, "UserInformation", user.uid);
                const userDoc = await getDoc(userRef);
                const userData = userDoc.data();

                if (userData && userData.friendsList) {
                    setAllFriends(userData.friendsList);
                    console.log(allFriends);
                }
            } catch (error) {
                console.error("Error fetching user's friends:", error);
            }     
        };
        fetchFriends();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    // Helper function to get cached user data or fetch it
    const getCachedUserData = async (userId) => {
        if (userCache.current[userId]) {
            return userCache.current[userId];
        }
        try {
            const userDoc = await getDoc(doc(db, "UserInformation", userId));
            if (userDoc.exists()) {
                const userData = userDoc.data();
                userCache.current[userId] = userData;
                return userData;
            }
        } catch (error) {
            console.error('Error fetching user data:', error);
        }
        return null;
    };

    // Fetch conversations where the user is a participant
    useEffect(() => {
        if (!user) return;
        setIsLoading(true);
        
        const q = query(collection(db, "conversations"), where("participants", "array-contains", user.uid));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            // First, quickly set basic conversation data without waiting for async operations
            const basicChats = snapshot.docs.map((docSnap) => {
                const chatData = docSnap.data();
                const otherUserId = !chatData.isGroup 
                    ? chatData.participants.find(id => id !== user.uid) 
                    : null;
                
                return { 
                    id: docSnap.id, 
                    chatName: chatData.isGroup ? "Loading..." : "Loading...",
                    profilePic: null,
                    lastMessageTime: chatData.lastMessageTime || null,
                    lastMessageText: chatData.lastMessageText || null, // Use stored last message if available
                    isGroup: chatData.isGroup || false,
                    userId: otherUserId,
                    ...chatData 
                };
            });
            
            setConversations(basicChats);
            setIsLoading(false);

            // Then fetch additional data in the background
            const enrichConversations = async () => {
                const enrichedChats = await Promise.all(basicChats.map(async (chat) => {
                    // Use lastMessageText from conversation doc if available, otherwise fetch from subcollection
                    let lastMessageText = chat.lastMessageText;
                    
                    if (!lastMessageText) {
                        // Fallback: fetch last message from subcollection for older conversations
                        try {
                            const messagesRef = collection(db, "conversations", chat.id, "messages");
                            const lastMsgQuery = query(messagesRef, orderBy("timestamp", "desc"), limit(1));
                            const lastMsgSnapshot = await getDocs(lastMsgQuery);
                            if (!lastMsgSnapshot.empty) {
                                const msgData = lastMsgSnapshot.docs[0].data();
                                lastMessageText = msgData.text || '';
                            }
                        } catch (error) {
                            console.error('Error fetching last message:', error);
                        }
                    }

                    if (!chat.isGroup && chat.userId) {
                        // For individual chats - use cached user data
                        const otherUserData = await getCachedUserData(chat.userId);
                        return { 
                            ...chat,
                            chatName: otherUserData?.name || "Unknown User",
                            profilePic: otherUserData?.profilePictureUrl,
                            lastMessage: lastMessageText || '',
                        };
                    } else if (chat.isGroup) {
                        // For group chats
                        try {
                            const groupsRef = collection(db, "groups");
                            const groupQuery = query(groupsRef, where("conversationId", "==", chat.id));
                            const groupSnapshot = await getDocs(groupQuery);
                            
                            if (!groupSnapshot.empty) {
                                const groupDoc = groupSnapshot.docs[0];
                                const groupData = groupDoc.data();
                                return { 
                                    ...chat,
                                    chatName: groupData.title || "Unknown Group",
                                    profilePic: groupData.image,
                                    groupId: groupDoc.id,
                                    lastMessage: lastMessageText || '',
                                };
                            }
                        } catch (error) {
                            console.error('Error fetching group data:', error);
                        }
                        return { ...chat, chatName: "Unknown Group", lastMessage: lastMessageText || '' };
                    }
                    return { ...chat, lastMessage: lastMessageText || '' };
                }));
                setConversations(enrichedChats);
            };

            enrichConversations();
        });

        return () => unsubscribe();
    }, [user]);

    const formatMessageTime = (timestamp) => {
        if (!timestamp) return '';
        
        const messageDate = timestamp.toDate();
        const now = new Date();
        const diffMinutes = Math.floor((now - messageDate) / (1000 * 60));
        
        if (diffMinutes < 60) {
            return `${diffMinutes}m ago`;
        } else if (diffMinutes < 24 * 60) {
            const hours = Math.floor(diffMinutes / 60);
            return `${hours}h ago`;
        } else {
            return messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        }
    };

    // Fetch messages for the selected chat
    useEffect(() => {
        if (!selectedChat ) return;
        if (typeof selectedChat === 'object' && selectedChat.isNew) { 
            setMessages([]);
            return;
        }

        const messagesRef = collection(db, "conversations", selectedChat, "messages");
        const q = query(messagesRef, orderBy("timestamp", "asc"));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const msgs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setMessages(msgs);
            
            // Get unique sender IDs from messages that we don't have cached
            const senderIds = [...new Set(msgs.map(msg => msg.senderId))]
                .filter(senderId => !userCache.current[senderId]);
            
            // Fetch user info in background without blocking message display
            if (senderIds.length > 0) {
                Promise.all(senderIds.map(async (senderId) => {
                    const userData = await getCachedUserData(senderId);
                    if (userData) {
                        setSenderNames(prev => ({ ...prev, [senderId]: userData.name }));
                        setSenderProfilePics(prev => ({ ...prev, [senderId]: userData.profilePictureUrl }));
                    }
                }));
            }
        });

        return () => unsubscribe();
    }, [selectedChat]);

    // Send a message
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [messageContent, setMessageContent] = useState('');
    const [showFormattingMenu, setShowFormattingMenu] = useState(false);
    const emojiPickerRef = useRef(null);
    const inputRef = useRef(null);
    const formattingMenuRef = useRef(null);

    // Close formatting menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
                setShowEmojiPicker(false);
            }
            if (formattingMenuRef.current && !formattingMenuRef.current.contains(event.target)) {
                setShowFormattingMenu(false);
            }
        };

        if (showEmojiPicker || showFormattingMenu) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showEmojiPicker, showFormattingMenu]);

    const toggleFormattingMenu = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setShowFormattingMenu(prev => !prev);
    };

    const applyFormatting = (format) => {
        const input = inputRef.current;
        if (!input) return;

        const start = input.selectionStart;
        const end = input.selectionEnd;
        const selectedText = messageContent.substring(start, end);
        let newText = '';

        switch (format) {
            case 'bold':
                newText = `<b>${selectedText}</b>`;
                break;
            case 'italic':
                newText = `<i>${selectedText}</i>`;
                break;
            case 'underline':
                newText = `<u>${selectedText}</u>`;
                break;
            case 'strikethrough':
                newText = `<s>${selectedText}</s>`;
                break;
            default:
                return;
        }

        const newContent = messageContent.substring(0, start) + newText + messageContent.substring(end);
        setMessageContent(newContent);
        setShowFormattingMenu(false);
    };

    const toggleEmojiPicker = useCallback(async (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Lazy load emoji data on first open
        if (!emojiData) {
            try {
                const data = await getEmojiData();
                setEmojiData(data);
            } catch (error) {
                console.error('Failed to load emoji data:', error);
                return;
            }
        }
        
        setShowEmojiPicker(prev => !prev);
    }, [emojiData]);

    const handleEmojiSelect = (emoji) => {
        const input = inputRef.current;
        if (input) {
            const start = input.selectionStart;
            const end = input.selectionEnd;
            const newValue = messageContent.substring(0, start) + emoji.native + messageContent.substring(end);
            setMessageContent(newValue);
            
            // Set cursor position after emoji
            setTimeout(() => {
                input.selectionStart = input.selectionEnd = start + emoji.native.length;
                input.focus();
            }, 0);
        }
    };

    const sendMessage = async (e) => {
        e.preventDefault();
        if (!messageContent.trim() || !selectedChat) return;

        try {
            let chatId = selectedChat;
        
            if (typeof selectedChat === 'object' && selectedChat.isNew) {
                const newChatRef = doc(collection(db, "conversations"));
                await setDoc(newChatRef, {
                    participants: selectedChat.participants,
                    isGroup: false
                });
                chatId = newChatRef.id;
                setSelectedChat(chatId);
            }

            const messageText = messageContent.trim();
            const timestamp = new Date();

            await addDoc(collection(db, "conversations", chatId, "messages"), {
                text: messageText,
                senderId: user.uid,
                timestamp: timestamp,
                isFormatted: messageContent.includes('<b>') || messageContent.includes('<i>') || 
                           messageContent.includes('<u>') || messageContent.includes('<s>')
            });

            // Update the conversation document with last message info
            await updateDoc(doc(db, "conversations", chatId), {
                lastMessageTime: timestamp,
                lastMessageText: messageText.length > 50 ? messageText.substring(0, 50) + '...' : messageText
            });

            setMessageContent("");
        } catch (error) {
            console.error("Error sending message:", error);
        }
    };

    // Start a new conversation
    const startConversation = async (receiverId) => {
        if (receiverId === user.uid) return;
        console.log("Closing drop down");
        setIsDropdownOpen(false);
        setFriendSearchQuery('');

        // Check if a conversation already exists
        const q = query(collection(db, "conversations"), 
            where("participants", "array-contains", user.uid));
        const existingChats = await getDocs(q);

        const chat = existingChats.docs.find(doc => {
            const chatData = doc.data();
            // Check that it is not a group chat and that the receiver is in the conversation
            return chatData.participants.includes(receiverId) && !chatData.isGroup;
        });
        
        if (chat) {
            console.log("Setting selected chat");
            setSelectedChat(chat.id);
        } else {
            console.log("setting!")
            setSelectedChat({ participants: [user.uid, receiverId], isNew: true});
            console.log("set!");
        }
        
        // Switch to chat view on mobile
        if (isMobile) setMobileView('chat');
    };

    // Handle selecting a chat (for mobile navigation)
    const handleSelectChat = (chatId) => {
        setSelectedChat(chatId);
        if (isMobile) setMobileView('chat');
    };

    // Handle back button on mobile
    const handleBackToList = () => {
        setMobileView('list');
    };

    // Filter friends based on search query
    const filteredFriends = allFriends.filter(friend => 
        friend.name?.toLowerCase().includes(friendSearchQuery.toLowerCase())
    );

    // Get display friends (first 5 if no search, all filtered if searching)
    const displayFriends = friendSearchQuery ? filteredFriends : filteredFriends.slice(0, 5);

    // Modify renderMessage function
    const renderMessage = (msg) => {
        const isCurrentUser = msg.senderId === user.uid;
        const messageTime = msg.timestamp ? new Date(msg.timestamp.toDate()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';

        if (msg.type === 'shared_post') {
            return (
                <div key={msg.id} className={`message ${isCurrentUser ? "sent" : "received"}`}>
                    <div className="message-content">
                        {!isCurrentUser && (
                            <div className="message-sender">
                                <Avatar
                                    src={senderProfilePics[msg.senderId]}
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        marginRight: 1,
                                        bgcolor: '#E9505F'
                                    }}
                                >
                                    {senderNames[msg.senderId]?.[0] || '?'}
                                </Avatar>
                                <span className="sender-name">{senderNames[msg.senderId] || 'Unknown'}</span>
                            </div>
                        )}
                        <div className="message-bubble">
                            <SharedPostLink 
                                postId={msg.postId}
                                caption={msg.caption}
                                senderName={msg.senderName}
                            />
                            <span className="message-time">{messageTime}</span>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div key={msg.id} className={`message ${isCurrentUser ? "sent" : "received"}`}>
                <div className="message-content">
                    {!isCurrentUser && (
                        <div className="message-sender">
                            <Avatar
                                src={senderProfilePics[msg.senderId]}
                                sx={{
                                    width: 32,
                                    height: 32,
                                    marginRight: 1,
                                    bgcolor: '#E9505F'
                                }}
                            >
                                {senderNames[msg.senderId]?.[0] || '?'}
                            </Avatar>
                            <span className="sender-name">{senderNames[msg.senderId] || 'Unknown'}</span>
                        </div>
                    )}
                    <div className="message-bubble">
                        {msg.isFormatted ? (
                            <div dangerouslySetInnerHTML={{ __html: msg.text }} />
                        ) : (
                            <p>{msg.text}</p>
                        )}
                        <span className="message-time">{messageTime}</span>
                    </div>
                </div>
            </div>
        );
    };

    const handleViewProfile = (userId) => {
        if (userId) {
            navigate(`/profile/${userId}`);
        }
    };

    // Determine if we should show sidebar or chat on mobile
    const showSidebar = !isMobile || mobileView === 'list';
    const showChatWindow = !isMobile || mobileView === 'chat';

    return (
        <Layout pageTitle="Messages">
            <div className="page-content">
                <div className="messages-container">
                    {/* Left Sidebar */}
                    {showSidebar && (
                    <div className={`sidebar ${isMobile ? 'mobile-full' : ''}`}>
                        <div className="sidebar-header">
                            <button 
                                className="new-chat-btn-round" 
                                onClick={() => toggleDropdown()}
                                title="New Chat"
                            >
                                +
                            </button>
                        </div>

                        <div className="chat-list-scroll">
                        {isLoading ? (
                            <div className="loading-chats">
                                <div className="loading-spinner"></div>
                                <span>Loading chats...</span>
                            </div>
                        ) : conversations.length === 0 ? (
                            <p className="no-chats">No conversations yet. Start a new chat!</p>
                        ) : (
                            conversations.map(chat => (
                                <div 
                                    key={chat.id} 
                                    className={`chat-list-item ${selectedChat === chat.id ? 'active' : ''}`}
                                    onClick={() => handleSelectChat(chat.id)}
                                >
                                    <div className="chat-item">
                                        <Avatar
                                            src={chat.profilePic}
                                            sx={{
                                                width: 48,
                                                height: 48,
                                                bgcolor: '#E9505F'
                                            }}
                                        >
                                            {chat.chatName?.[0] || '?'}
                                        </Avatar>
                                        <div className="chat-item-info">
                                            <div className="chat-item-top">
                                                <span className="chat-item-name">{chat.chatName}</span>
                                                {chat.lastMessageTime && (
                                                    <span className="chat-item-time">
                                                        {formatMessageTime(chat.lastMessageTime)}
                                                    </span>
                                                )}
                                            </div>
                                            {chat.lastMessage && (
                                                <span className="chat-item-preview">
                                                    {chat.lastMessage.length > 40 
                                                        ? chat.lastMessage.substring(0, 40) + '...' 
                                                        : chat.lastMessage}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                        </div>
                    </div>
                    )}

                    {/* Chat Window */}
                    {showChatWindow && (
                    <div className={`chat-window ${isMobile ? 'mobile-full' : ''}`}>
                        {selectedChat ? (
                            <>
                                {/* Chat Header - Fixed */}
                                <div className="chat-header fixed-header">
                                    {isMobile && (
                                        <button 
                                            className="back-button"
                                            onClick={handleBackToList}
                                        >
                                            <ArrowBack sx={{ fontSize: 24 }} />
                                        </button>
                                    )}
                                    {conversations.find(chat => chat.id === selectedChat) && (
                                        <>
                                            <div className="chat-user-info">
                                                <Avatar
                                                    src={conversations.find(chat => 
                                                        chat.id === selectedChat
                                                    )?.profilePic}
                                                    sx={{
                                                        width: 40,
                                                        height: 40,
                                                        marginRight: 1,
                                                        bgcolor: '#E9505F'
                                                    }}
                                                >
                                                    {conversations.find(chat => 
                                                        chat.id === selectedChat
                                                    )?.chatName[0] || '?'}
                                                </Avatar>
                                                <div className="user-status">
                                                    <span className="user-name">
                                                        {conversations.find(chat => 
                                                            chat.id === selectedChat
                                                        )?.chatName}
                                                    </span>
                                                    <span className="online-status">
                                                        <span className="status-dot"></span>
                                                        Online
                                                    </span>
                                                </div>
                                            </div>
                                            {!conversations.find(chat => 
                                                chat.id === selectedChat
                                            )?.isGroup && (
                                                <button 
                                                    className="view-profile-btn"
                                                    onClick={() => handleViewProfile(conversations.find(chat => chat.id === selectedChat)?.userId)}
                                                >
                                                    Profile
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>
                                <div className="messages-list-container">
                                    <div className="messages-list">
                                        {messages.map(renderMessage)}
                                    </div>
                                </div>
                                <form onSubmit={sendMessage} className="message-input fixed-footer">
                                    <div className="message-input-container">
                                        <button 
                                            type="button"
                                            className="format-button" 
                                            onClick={toggleFormattingMenu}
                                        >
                                            <MoreVert sx={{ fontSize: 20 }} />
                                        </button>
                                        <input
                                            ref={inputRef}
                                            type="text"
                                            value={messageContent}
                                            onChange={(e) => setMessageContent(e.target.value)}
                                            placeholder="Type a message..."
                                        />
                                        <button 
                                            type="button"
                                            className="emoji-button" 
                                            onClick={toggleEmojiPicker}
                                        >
                                            <EmojiEmotions sx={{ fontSize: 20 }} />
                                        </button>
                                        <button 
                                            type="submit"
                                            className="send-button" 
                                            disabled={!messageContent.trim()}
                                        >
                                            <img src={SendIcon} alt="Send" />
                                        </button>
                                        {showFormattingMenu && (
                                            <div className="formatting-menu" ref={formattingMenuRef}>
                                                <button onClick={() => applyFormatting('bold')} className="format-option">
                                                    <b>B</b>
                                                </button>
                                                <button onClick={() => applyFormatting('italic')} className="format-option">
                                                    <i>I</i>
                                                </button>
                                                <button onClick={() => applyFormatting('underline')} className="format-option">
                                                    <u>U</u>
                                                </button>
                                                <button onClick={() => applyFormatting('strikethrough')} className="format-option">
                                                    <s>S</s>
                                                </button>
                                            </div>
                                        )}
                                        {showEmojiPicker && emojiData && (
                                            <div className="emoji-picker-container" ref={emojiPickerRef}>
                                                <Suspense fallback={
                                                    <div style={{ padding: '20px', textAlign: 'center' }}>
                                                        Loading emojis...
                                                    </div>
                                                }>
                                                    <EmojiPicker
                                                        data={emojiData}
                                                        onEmojiSelect={handleEmojiSelect}
                                                        theme="light"
                                                        autoFocus={true}
                                                        emojiSize={20}
                                                        emojiButtonSize={28}
                                                        perLine={8}
                                                    />
                                                </Suspense>
                                            </div>
                                        )}
                                    </div>
                                </form>
                            </>
                        ) : (
                            <p className="select-chat-prompt">Select a chat to start messaging.</p>
                        )}
                    </div>
                    )}
                </div>
            </div>


            {isDropdownOpen && (
            <div className="mobile-popup-overlay">
                <div
                    className="mobile-popup" 
                    ref={dropdownRef}
                >
                    <div className="popup-header">
                        <h3>Start a new chat</h3>
                        <button
                            className="messages-popup-close"
                            onClick={() => {
                                setIsDropdownOpen(false);
                                setFriendSearchQuery('');
                            }}
                        >
                            <Close sx={{ fontSize: 20 }} />
                        </button>
                    </div>
                    <div className="popup-search">
                        <input
                            type="text"
                            placeholder="Search friends..."
                            value={friendSearchQuery}
                            onChange={(e) => setFriendSearchQuery(e.target.value)}
                            className="friend-search-input"
                            autoFocus
                        />
                    </div>
                    <div className="popup-scroll">
                        {allFriends.length > 0 ? (
                            displayFriends.length > 0 ? (
                                <>
                                    {displayFriends.map((u) => (
                                        <div key={u.id} className="popup-chat-item" onClick={() => {
                                            startConversation(u.id)
                                        }}>
                                            <Avatar
                                                src={u.profilePictureUrl}
                                                sx={{ width: 40, height: 40, bgcolor: '#E9505F' }}
                                            >
                                                {u.name?.[0] || '?'}
                                            </Avatar>
                                            <span>{u.name}</span>
                                        </div>
                                    ))}
                                    {!friendSearchQuery && allFriends.length > 5 && (
                                        <p className="search-hint">Search to find more friends...</p>
                                    )}
                                </>
                            ) : (
                                <p className="no-friends">No friends found matching "{friendSearchQuery}"</p>
                            )
                        ) : (
                            <p className="no-friends">No friends available. Add friends from the Friends page!</p>
                        )}
                    </div>
                </div>
            </div>
            )} 

        </Layout>
    );
};

export default MessagesPage;
